import { useToast } from '@orbind/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, apiBase, webTokenStorage } from '../../lib/api.ts'
import { ehVisaoDeQuadro, tirarCartaoDaVisao } from '../kanban/quadro-em-cache.ts'

/**
 * Hooks de dados da funcionalidade de Itens de Trabalho (SPEC-20260807-1152).
 * Toda mutacao invalida o detalhe agregado — a tela recarrega de uma fonte so.
 */

export type WorkItemDetail = Awaited<
  ReturnType<Awaited<ReturnType<(typeof api)['work-items'][':id']['$get']>>['json']>
>

export function useWorkItemDetail(id: string | null) {
  return useQuery({
    queryKey: ['work-item', id],
    queryFn: async () => (await api['work-items'][':id'].$get({ param: { id: id! } })).json(),
    enabled: id !== null,
  })
}

/** Tamanho da página quando a lista lê todas (o teto do servidor). */
export const ITENS_POR_PAGINA = 500

/**
 * Lista do explorer. Com `kanbanBoardId`, vem recortada pela configuracao
 * daquele quadro (SPEC-20261003-1059) — e o que a Lista do quadro mostra.
 *
 * Com `todos`, lê TODAS as páginas (SPEC-20261006-2244-todos-os-itens-na-lista):
 * a lista de Itens mostra todo item do workspace que a pessoa pode ver —
 * públicos e privados com acesso, recorte que o servidor faz. Antes vinham só
 * os 100 alterados por último, e busca e filtros rodavam sobre esses 100: item
 * mais antigo sumia sem explicação. Sem `todos` (o seletor do checklist), uma
 * página só, com a busca no servidor.
 */
/** Os pais que faltam na lista, no formato dela (ver a rota /work-items/ancestrais). */
async function buscarAncestrais(ids: string[]) {
  const lotes: string[][] = []
  for (let i = 0; i < ids.length; i += 300) lotes.push(ids.slice(i, i + 300))
  const respostas = await Promise.all(
    lotes.map(async (lote) => (await api['work-items'].ancestrais.$get({ query: { ids: lote.join(',') } })).json()),
  )
  return respostas.flatMap((r) => r.items)
}

export function useWorkItemList(params: { q?: string; kanbanBoardId?: string; todos?: boolean }) {
  return useQuery({
    queryKey: ['work-items', 'explorer', params],
    queryFn: async () => {
      const pagina = async (offset: number, limit: number) =>
        (
          await api['work-items'].$get({
            query: {
              scope: 'all',
              limit,
              offset,
              ...(params.q ? { q: params.q } : {}),
              ...(params.kanbanBoardId ? { kanbanBoardId: params.kanbanBoardId } : {}),
            },
          })
        ).json()

      if (!params.todos) return pagina(0, 100)

      // a primeira diz o total; as demais vêm juntas, em paralelo
      const primeira = await pagina(0, ITENS_POR_PAGINA)
      const offsets: number[] = []
      for (let o = ITENS_POR_PAGINA; o < primeira.total; o += ITENS_POR_PAGINA) offsets.push(o)
      const demais = await Promise.all(offsets.map((o) => pagina(o, ITENS_POR_PAGINA)))

      // um item alterado entre uma página e outra pode vir em duas: fica um só
      const vistos = new Set<string>()
      const items = [primeira, ...demais]
        .flatMap((p) => p.items)
        .filter((item) => (vistos.has(item.id) ? false : (vistos.add(item.id), true)))

      // PAIS QUE FALTAM (SPEC-20261008-1226-arvore-recursiva-e-avulsos): o pai arquivado, o privado
      // de outra pessoa ou o de fora do recorte não vem na lista — sem ele o
      // filho subia para a raiz. Vêm à parte, só para a árvore os encaixar.
      const naLista = new Set(items.map((i) => i.id))
      const faltam = [...new Set(items.flatMap((i) => (i.parentWorkItemId && !naLista.has(i.parentWorkItemId) ? [i.parentWorkItemId] : [])))]
      let ancestrais: Awaited<ReturnType<typeof buscarAncestrais>> = []
      if (faltam.length > 0) {
        try {
          ancestrais = await buscarAncestrais(faltam)
        } catch {
          // sem os pais a lista ainda abre — só com o filho na raiz, como antes
        }
      }
      return { ...primeira, items, ancestrais, limit: items.length, offset: 0 }
    },
  })
}

export function useLabels() {
  return useQuery({
    queryKey: ['labels'],
    queryFn: async () => (await api['work-items'].labels.all.$get()).json(),
  })
}

// -------------------------------------------------- Filtro de uma visao da lista (SPEC-20261103-1418)

/** Criterios do filtro de uma visao da lista de Itens — aplicados em conjunto (E). */
export type QuickFilterCriteria = {
  mine?: boolean
  overdue?: boolean
  statusId?: string | null
  typeId?: string | null
  priorityId?: string | null
  assigneeId?: string | null
  /** Construtor CAMPO/OPERADOR/VALOR (mesmo do Kanban), opcional. */
  advanced?: import('@orbind/core').SavedFilter
}

/**
 * Mutacao generica que invalida o detalhe (e listas) ao concluir.
 *
 * Erro SEMPRE vira toast (2026-08-30): sem isso, uma acao recusada pela API
 * (ex.: "Converter em item" com 422) simplesmente nao acontecia, sem nenhum
 * aviso — a tela ficava igual e o usuario nao tinha como saber o motivo.
 * Quem precisa de outra reacao passa o proprio `onError` na chamada de
 * `.mutate()`, que roda depois deste.
 */
/**
 * O item saiu (arquivado ou excluído): some NA HORA de todo quadro já carregado
 * e os quadros ficam marcados para buscar de novo (SPEC-20261027-1941-quadro-mais-rapido).
 * Antes o cartão continuava no quadro até a próxima busca completa.
 */
export function tirarItemDosQuadros(queryClient: ReturnType<typeof useQueryClient>, itemId: string) {
  queryClient.setQueriesData<{ columns: { id: string; cards: { id: string }[] }[] }>(
    { predicate: (q) => ehVisaoDeQuadro(q.queryKey) },
    (visao) => (visao?.columns ? tirarCartaoDaVisao(visao, itemId) : visao),
  )
  void queryClient.invalidateQueries({ predicate: (q) => ehVisaoDeQuadro(q.queryKey) })
}

export function useWiMutation<TInput>(id: string, run: (input: TInput) => Promise<unknown>) {
  const queryClient = useQueryClient()
  const toast = useToast()
  return useMutation({
    mutationFn: run,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['work-item', id] })
      queryClient.invalidateQueries({ queryKey: ['work-items'] })
      queryClient.invalidateQueries({ queryKey: ['labels'] })
      // o que muda no item (status, título, responsável…) muda o cartão: os
      // quadros buscam de novo ao voltar a eles (SPEC-20261027-1941-quadro-mais-rapido)
      queryClient.invalidateQueries({ predicate: (q) => ehVisaoDeQuadro(q.queryKey) })
    },
    onError: (erro) => {
      toast.push({
        tone: 'danger',
        message: erro instanceof Error ? erro.message : 'Não foi possível concluir a ação.',
      })
    },
  })
}

/** Upload multipart de anexo (fora do cliente RPC por ser FormData). */
export async function uploadWorkItemFile(workItemId: string, file: File) {
  const form = new FormData()
  form.append('file', file)
  const baseUrl = apiBase()
  const response = await fetch(`${baseUrl}/work-items/${workItemId}/files`, {
    method: 'POST',
    headers: { authorization: `Bearer ${webTokenStorage.get() ?? ''}` },
    body: form,
  })
  if (!response.ok) throw new Error('Falha no upload')
  return response.json()
}

/**
 * Sobe uma imagem da descricao (rich text) e devolve a URL a gravar no HTML.
 * A URL vem relativa (/public/work-item-image/:id): em producao a propria API
 * serve; em dev o Vite faz proxy de /public.
 */
export async function uploadWorkItemImage(file: File): Promise<string> {
  const form = new FormData()
  form.append('file', file)
  const response = await fetch(`${apiBase()}/work-items/images`, {
    method: 'POST',
    headers: { authorization: `Bearer ${webTokenStorage.get() ?? ''}` },
    body: form,
  })
  if (!response.ok) throw new Error('Falha no upload da imagem')
  const { url } = (await response.json()) as { url: string }
  return url
}

/** Valor guardado num campo complementar do tipo Anexo (SPEC-20261104-1542). */
export type AnexoDeCampo = { key: string; name: string; mimeType: string; sizeBytes: number }

export function ehAnexoDeCampo(v: unknown): v is AnexoDeCampo {
  return (
    typeof v === 'object' && v !== null && typeof (v as AnexoDeCampo).key === 'string' && typeof (v as AnexoDeCampo).name === 'string'
  )
}

/** Envia o arquivo de um campo Anexo para o R2 e devolve o valor a guardar no campo. */
export async function uploadCampoAnexo(file: File): Promise<AnexoDeCampo> {
  const form = new FormData()
  form.append('file', file)
  const response = await fetch(`${apiBase()}/work-items/campo-anexo`, {
    method: 'POST',
    headers: { authorization: `Bearer ${webTokenStorage.get() ?? ''}` },
    body: form,
  })
  if (!response.ok) {
    const d = (await response.json().catch(() => ({}))) as { error?: { message?: string } }
    throw new Error(d.error?.message ?? 'Falha no upload do anexo')
  }
  const { anexo } = (await response.json()) as { anexo: AnexoDeCampo }
  return anexo
}

/**
 * Abre o anexo de campo numa aba nova (PDF e imagem o navegador mostra; o
 * resto ele baixa). O download exige sessao, entao busca com o token e abre
 * o blob — como `downloadFile`.
 */
export async function abrirAnexoDeCampo(anexo: AnexoDeCampo) {
  const q = new URLSearchParams({ key: anexo.key, name: anexo.name, tipo: anexo.mimeType, abrir: '1' })
  const response = await fetch(`${apiBase()}/work-items/campo-anexo/download?${q}`, {
    headers: { authorization: `Bearer ${webTokenStorage.get() ?? ''}` },
  })
  if (!response.ok) throw new Error('Não foi possível abrir o anexo.')
  const blob = await response.blob()
  const url = URL.createObjectURL(new Blob([blob], { type: anexo.mimeType }))
  const aba = window.open(url, '_blank', 'noopener')
  if (!aba) {
    // bloqueador de pop-up: cai no download com o nome certo
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = anexo.name
    anchor.click()
  }
  setTimeout(() => URL.revokeObjectURL(url), 60_000)
}

export function fileDownloadUrl(fileId: string) {
  const baseUrl = apiBase()
  return `${baseUrl}/work-items/files/${fileId}/download`
}

/** Baixa com o token no header (o download exige sessao). */
export async function downloadFile(fileId: string, name: string) {
  const response = await fetch(fileDownloadUrl(fileId), {
    headers: { authorization: `Bearer ${webTokenStorage.get() ?? ''}` },
  })
  const blob = await response.blob()
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = name
  anchor.click()
  URL.revokeObjectURL(url)
}

/**
 * Colunas "PARA TODOS" da lista de Itens sem filtro (SPEC-20261103-1217): do
 * quadro, quando a lista está dentro de um; do workspace, fora. `podePublicar`
 * diz se esta pessoa pode pôr colunas para todos (dono do quadro / quem
 * administra o workspace) — só a ela a tela pergunta "para quem?".
 */
export function useColunasParaTodos(quadroId: string | null) {
  const queryClient = useQueryClient()
  const chave = ['colunas-para-todos', quadroId ?? 'workspace']
  type Resposta = { columns: string[]; canPublish: boolean }
  const consulta = useQuery({
    queryKey: chave,
    queryFn: async (): Promise<Resposta> => {
      const r = quadroId
        ? await api.kanbans[':id']['shared-columns'].$get({ param: { id: quadroId } })
        : await api.workspaces.current['shared-columns'].$get()
      // sem acesso, ou rota indisponível: a lista segue só com as da pessoa
      if (!r.ok) return { columns: [], canPublish: false }
      return (await r.json()) as Resposta
    },
  })
  const gravar = useMutation({
    mutationFn: async (columns: string[]): Promise<Resposta> => {
      const r = quadroId
        ? await api.kanbans[':id']['shared-columns'].$put({ param: { id: quadroId }, json: { columns } })
        : await api.workspaces.current['shared-columns'].$put({ json: { columns } })
      const corpo = (await r.json().catch(() => null)) as (Resposta & { error?: { message?: string } }) | null
      if (!r.ok || !corpo) throw new Error(corpo?.error?.message ?? 'Não foi possível pôr a coluna para todos.')
      return corpo
    },
    onSuccess: (dados) => queryClient.setQueryData(chave, dados),
  })
  return {
    /** null enquanto carrega: ainda não dá para entregar nem tirar nada. */
    colunas: consulta.data?.columns ?? null,
    podePublicar: consulta.data?.canPublish ?? false,
    gravar,
  }
}
